import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef, Children } from 'react';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import { screenReaderStyles } from '@paypalcorp/pp-react-screen-reader';
import { CheckmarkIcon, ChevronUpIcon, ChevronDownIcon } from '@paypalcorp/pp-react-icons';
import { cx, uniqueId, hasWindow } from '@paypalcorp/pp-react-utils';
import * as styles from './styles.css';
export { styles as buttonStyles };
import { breakpoints } from '@paypalcorp/pp-react-theme';

var _excluded = ["isMenu", "isOpen", "isSmall", "className"],
  _excluded2 = ["variant", "size", "inverse", "secondary", "tertiary", "isMenu", "isOpen", "btnState", "screenReaderText", "className", "children", "as", "iconComponent", "fullWidthOnMobile"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var buttonVariantStyles = ['primary', 'secondary', 'overlay', 'tertiary'];
var AffordanceIcon = function (_ref) {
  var isMenu = _ref.isMenu,
    isOpen = _ref.isOpen,
    isSmall = _ref.isSmall,
    className = _ref.className,
    rest = _objectWithoutProperties(_ref, _excluded);
  if (!isMenu) {
    return null;
  }
  var Icon = isOpen ? ChevronUpIcon : ChevronDownIcon;
  return /*#__PURE__*/React.createElement(Icon, _extends({
    className: className
  }, rest, {
    size: isSmall ? 'xs' : 'sm',
    "data-ppui": "true"
  }));
};
var Button = forwardRef(function (_ref2, ref) {
  var variant = _ref2.variant,
    size = _ref2.size,
    inverse = _ref2.inverse,
    secondary = _ref2.secondary,
    tertiary = _ref2.tertiary,
    isMenu = _ref2.isMenu,
    isOpen = _ref2.isOpen,
    _ref2$btnState = _ref2.btnState,
    btnState = _ref2$btnState === void 0 ? 'initial' : _ref2$btnState,
    screenReaderText = _ref2.screenReaderText,
    className = _ref2.className,
    children = _ref2.children,
    as = _ref2.as,
    iconComponent = _ref2.iconComponent,
    _ref2$fullWidthOnMobi = _ref2.fullWidthOnMobile,
    fullWidthOnMobile = _ref2$fullWidthOnMobi === void 0 ? true : _ref2$fullWidthOnMobi,
    rest = _objectWithoutProperties(_ref2, _excluded2);
  var isSizeSmall = size === 'sm';
  var isStateInitial = btnState && btnState === 'initial';

  /**
   * Get loading spinner component props when the button is in processing state.
   * @returns LoadingSpinnerProps
   */
  var getLoadingSpinnerProps = function () {
    var loadingSpinnerProps = {};
    if (btnState !== 'processing') {
      return loadingSpinnerProps;
    }
    loadingSpinnerProps.as = 'span';
    if (size === 'sm') {
      loadingSpinnerProps.size = 'xs';
    }
    if (variant && (inverse && variant === 'secondary' || !inverse && variant === 'primary')) {
      loadingSpinnerProps.inverse = true;
    } else if (!variant && (secondary && inverse || !secondary && !inverse)) {
      loadingSpinnerProps.inverse = true;
    }
    return loadingSpinnerProps;
  };
  /**
   * Get color styles for deprecated props.
   * @deprecated
   * @param isSecondary
   * @param isInverse
   * @returns ButtonVariantStyle | ButtonInverseVariantStyle
   */
  var getButtonTypeStyles = function (isSecondary, isInverse) {
    var variant = isSecondary ? 'secondary' : 'primary';
    return isInverse ? "".concat(variant, "Inverse") : variant;
  };

  /**
   * Get button variant styles based on the 'variant' prop.
   * @param variant
   * @returns ButtonVariantStyle | ButtonInverseVariantStyle
   */
  var getVariantStyles = function (variant) {
    if (inverse && (variant === 'primary' || variant === 'secondary')) {
      return "".concat(variant, "Inverse");
    }
    return buttonVariantStyles.includes(variant) ? variant : 'primary';
  };
  var componentProps = _objectSpread({}, rest);
  if (isOpen !== undefined) {
    componentProps['aria-expanded'] = isOpen;
  }
  componentProps['tab-index'] = 0;
  componentProps['disabled'] = !isStateInitial;
  componentProps['ref'] = ref;
  componentProps['data-ppui-info'] = '';
  var Component = as || 'button';
  if (isSizeSmall) {
    Component = 'div';
  }
  var buttonComponent = /*#__PURE__*/React.createElement(Component, _extends({
    className: function getStyleClassNames() {
      var classNames;
      // Not adding the custom className when the `size = 'sm'` as the same is getting added while rendering. See 'ComponentAs' below.
      var customClassName = isSizeSmall ? '' : className;
      if (variant) {
        var colorStyles = getVariantStyles(variant);
        classNames = cx(styles.btnBase, isSizeSmall ? styles.btnSmall : styles.btnLarge, styles.buttonsType[colorStyles], fullWidthOnMobile && styles.btnFullWidth, btnState === 'success' && styles.successState, btnState === 'processing' && styles.processingState, customClassName);
      } else {
        // This is backward compatible code for props primary, secondary and tertiary.
        if (tertiary) {
          classNames = cx(styles.buttonsType['tertiary'], isSizeSmall ? styles.tertiarySmall : styles.btnLarge, customClassName);
        } else {
          var _colorStyles = getButtonTypeStyles(secondary, inverse);
          classNames = cx(styles.btnBase, isSizeSmall ? styles.btnSmall : styles.btnLarge, styles.buttonsType[_colorStyles], fullWidthOnMobile && styles.btnFullWidth, btnState === 'success' && styles.successState, btnState === 'processing' && styles.processingState, customClassName);
        }
      }
      return classNames;
    }(),
    id: uniqueId('button_')
    // Not using any props when the size is small, as this will be used inside a 'Polymorphic' / 'Button' component.
  }, isSizeSmall ? {} : _objectSpread({}, componentProps), {
    "data-ppui": "true"
  }), function renderChildren() {
    var screenReaderTextElement;
    if (!isStateInitial) {
      screenReaderTextElement = screenReaderText || btnState;
    }
    var successIconClassNames = cx(styles.successStateIcon, isSizeSmall && styles.successStateIconSm);
    var buttonCheckmarkIcon = /*#__PURE__*/React.createElement(CheckmarkIcon, {
      className: successIconClassNames,
      size: isSizeSmall ? 'sm' : 'md',
      "data-ppui": "true"
    });
    var iconMarkup = null;
    if (iconComponent) {
      iconMarkup = /*#__PURE__*/React.createElement(iconComponent, {
        className: styles.icon,
        size: isSizeSmall ? 'xs' : 'sm',
        "data-ppui": "true"
      });
    }
    var overlayClassNames = cx(styles.btnOverlay, btnState === 'success' && styles.successStateOverlay);
    var stateOverlay = !isStateInitial ? /*#__PURE__*/React.createElement("span", {
      className: overlayClassNames,
      "data-ppui": "true"
    }, btnState === 'success' && buttonCheckmarkIcon, btnState === 'processing' && !tertiary && /*#__PURE__*/React.createElement(LoadingSpinner, _extends({}, getLoadingSpinnerProps(), {
      "data-ppui": "true"
    }))) : null;
    var assistiveText = screenReaderTextElement ? /*#__PURE__*/React.createElement("span", {
      role: "status",
      className: screenReaderStyles.screenReader,
      "data-ppui": "true"
    }, children, " ", screenReaderTextElement) : null;
    return /*#__PURE__*/React.createElement(React.Fragment, null, iconMarkup, children, /*#__PURE__*/React.createElement(AffordanceIcon, {
      isMenu: isMenu,
      isOpen: isOpen,
      isSmall: isSizeSmall,
      className: styles.affordance,
      "data-ppui": "true"
    }), stateOverlay, assistiveText);
  }());
  if (isSizeSmall) {
    return /*#__PURE__*/React.createElement(as || 'button', _extends({
      className: cx(styles.smallSizeTapContainer, btnState === 'initial' && styles.cursorPointer, className)
    }, componentProps, {
      "data-ppui": "true"
    }), buttonComponent);
  }
  return buttonComponent;
});

var ButtonGroup = function (_ref) {
  var className = _ref.className,
    _ref$fullWidthOnMobil = _ref.fullWidthOnMobile,
    fullWidthOnMobile = _ref$fullWidthOnMobil === void 0 ? true : _ref$fullWidthOnMobil,
    children = _ref.children;
  var childArray = Children.toArray(children);
  var breakPointValue = parseInt(breakpoints.mediaSm, 10) * 16;
  var innerWidth = hasWindow() && window.innerWidth;
  var computedChildren = children;
  if (breakPointValue && innerWidth > breakPointValue) {
    computedChildren = childArray.reverse();
  }
  var buttonGroupClassNames = cx(childArray.length && styles.buttonGroup[childArray.length], fullWidthOnMobile && styles.btnGroupFullWidth, className);
  return /*#__PURE__*/React.createElement("div", {
    className: buttonGroupClassNames,
    "data-ppui": "true"
  }, computedChildren);
};

export { Button, ButtonGroup };
